import React, { useEffect, useMemo, useState } from "react";
import hrApi from "./hrApi";
import { hrOpen } from "./HrCompliance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Send, Eye, Pencil, Upload, FileText, Plus, Trash2, Image as ImageIcon } from "lucide-react";

const ST = { completed: ["Completed", "bg-teal-100 text-teal-700"], in_progress: ["Opened", "bg-indigo-100 text-indigo-700"], sent: ["Sent", "bg-orange-100 text-orange-700"], not_sent: ["Not sent", "bg-slate-100 text-slate-500"] };
const LET = "ABCDEFGH";

function UploadSwi({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const save = async () => {
    if (!name.trim() || !file) return toast.error("Give it a name and choose the PDF");
    const fd = new FormData(); fd.append("name", name); fd.append("file", file);
    try { const r = await hrApi.post("/swi", fd); toast.success("Uploaded. Now add the quiz questions"); setOpen(false); onDone(r.data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  return (<>
    <Button data-testid="swi-upload-btn" variant="outline" onClick={() => setOpen(true)} className="h-9 rounded-full gap-1 text-xs"><Upload size={13} /> Upload new SWI</Button>
    <Dialog open={open} onOpenChange={setOpen}><DialogContent>
      <DialogHeader><DialogTitle>Upload a Standard Work Instruction</DialogTitle></DialogHeader>
      <Input data-testid="swi-upload-name" placeholder="e.g. Packing – Manual Handling" value={name} onChange={(e) => setName(e.target.value)} />
      <input data-testid="swi-upload-file" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
      <DialogFooter><Button data-testid="swi-upload-save" onClick={save}>Upload</Button></DialogFooter>
    </DialogContent></Dialog>
  </>);
}

const BASE = process.env.REACT_APP_BACKEND_URL || "";
function ImagePick({ mid, value, onChange, testid }) {
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await hrApi.post(`/swi/${mid}/image`, fd); onChange(r.data.name); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  return (
    <label className="shrink-0 w-24 h-16 rounded-lg border border-dashed border-slate-300 overflow-hidden cursor-pointer grid place-items-center text-[10px] text-slate-400 bg-slate-50" title="Change picture">
      {value ? <img src={`${BASE}/api/hr/public/swi-img/${value}`} alt="" className="w-full h-full object-cover" /> : <span className="inline-flex items-center gap-1"><ImageIcon size={12} /> Picture</span>}
      <input data-testid={testid} type="file" accept="image/*" className="hidden" onChange={pick} />
    </label>
  );
}

function LessonsEd({ m, lessons, set }) {
  const up = (i, k, v) => set(lessons.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-slate-700">Lesson cards (shown before the quiz, one point per line)</div>
      {lessons.map((x, i) => (
        <div key={x.id || i} className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-3 flex gap-3" data-testid={`swi-ed-l-${i}`}>
          <ImagePick mid={m.id} value={x.image} onChange={(v) => up(i, "image", v)} testid={`swi-ed-l-img-${i}`} />
          <div className="flex-1 space-y-2">
            <div className="flex gap-2"><Input value={x.title} onChange={(e) => up(i, "title", e.target.value)} placeholder="Card title" className="h-8" />
              <button onClick={() => set(lessons.filter((_, j) => j !== i))} className="text-rose-500"><Trash2 size={15} /></button></div>
            <Textarea value={x.body} onChange={(e) => up(i, "body", e.target.value)} className="text-sm min-h-[70px]" />
          </div>
        </div>
      ))}
      <Button data-testid="swi-ed-add-lesson" variant="outline" onClick={() => set([...lessons, { title: "", body: "", image: "" }])} className="gap-1 h-8 text-xs"><Plus size={13} /> Add lesson card</Button>
    </div>
  );
}

function QEditor({ m, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ name: m.name, trainer_name: m.trainer_name || "", read_pages: (m.read_pages || []).map((p) => p + 1).join(", "), lessons: (m.lessons || []).map((x) => ({ ...x })), questions: m.questions.map((q) => ({ ...q, options: [...q.options] })) }));
  const setQ = (i, k, v) => setF((s) => ({ ...s, questions: s.questions.map((q, j) => (j === i ? { ...q, [k]: v } : q)) }));
  const save = async () => {
    const pages = f.read_pages.split(/[,\s]+/).map((x) => parseInt(x, 10) - 1).filter((x) => x >= 0);
    try { await hrApi.put(`/swi/${m.id}`, { ...f, read_pages: pages }); toast.success("Saved"); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Edit training: {m.name}</DialogTitle></DialogHeader>
      <div className="grid sm:grid-cols-3 gap-2">
        <Input data-testid="swi-ed-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" />
        <Input data-testid="swi-ed-trainer" value={f.trainer_name} onChange={(e) => setF({ ...f, trainer_name: e.target.value })} placeholder="Trainer name" />
        <Input data-testid="swi-ed-pages" value={f.read_pages} onChange={(e) => setF({ ...f, read_pages: e.target.value })} placeholder={`Pages to read (1-${m.pages})`} />
      </div>
      <p className="text-[11px] text-slate-400">Pages: shown under "View original document" (or as the reading pages if there are no lesson cards). Never include the answer-key page. Tick the circle next to the correct option.</p>
      <LessonsEd m={m} lessons={f.lessons} set={(v) => setF((s) => ({ ...s, lessons: v }))} />
      <div className="text-sm font-bold text-slate-700 pt-2">Quiz questions</div>
      {f.questions.map((q, i) => (
        <div key={q.id || i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`swi-ed-q-${i}`}>
          <div className="flex gap-2"><span className="font-bold text-sm pt-2">Q{i + 1}</span><ImagePick mid={m.id} value={q.image} onChange={(v) => setQ(i, "image", v)} testid={`swi-ed-q-img-${i}`} /><Input value={q.q} onChange={(e) => setQ(i, "q", e.target.value)} />
            <button onClick={() => setF((s) => ({ ...s, questions: s.questions.filter((_, j) => j !== i) }))} className="text-rose-500" data-testid={`swi-ed-del-${i}`}><Trash2 size={15} /></button></div>
          {q.options.map((o, c) => (
            <div key={c} className="flex gap-2 items-center pl-6"><input type="radio" checked={q.correct === c} onChange={() => setQ(i, "correct", c)} className="accent-teal-600" />
              <span className="text-xs font-bold w-4">{LET[c]})</span><Input value={o} onChange={(e) => setQ(i, "options", q.options.map((x, k) => (k === c ? e.target.value : x)))} className="h-8 text-sm" /></div>
          ))}
          <div className="pl-6 flex gap-2">{q.options.length < 6 && <button className="text-xs text-indigo-600" onClick={() => setQ(i, "options", [...q.options, ""])}>+ option</button>}</div>
          <Textarea value={q.explain} onChange={(e) => setQ(i, "explain", e.target.value)} placeholder="Explanation shown when a wrong answer is chosen" className="text-sm min-h-[60px]" />
        </div>
      ))}
      <Button data-testid="swi-ed-add" variant="outline" onClick={() => setF((s) => ({ ...s, questions: [...s.questions, { q: "", options: ["", "", ""], correct: 0, explain: "" }] }))} className="gap-1"><Plus size={14} /> Add question</Button>
      <DialogFooter><Button data-testid="swi-ed-save" onClick={save}>Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function SendBar({ m, ids, onSent }) {
  const [ch, setCh] = useState({ sms: true, email: true });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const channels = Object.keys(ch).filter((k) => ch[k]);
    if (!ids.length || !channels.length) return toast.error("Select workers and at least one channel");
    setBusy(true);
    try {
      const r = await hrApi.post(`/swi/${m.id}/send`, { employee_ids: ids, channels });
      toast.success(`Sent to ${r.data.sent_count} worker(s)`);
      if (r.data.failed.length) toast.error(`${r.data.failed.length} could not be sent: ${r.data.failed.slice(0, 3).map((x) => `${x.employee_name} (${[x.sent.sms_error, x.sent.email_error].filter(Boolean).join("; ") || "failed"})`).join(", ")}${r.data.failed.length > 3 ? "…" : ""}`, { duration: 9000 });
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="swi-sendbar">
      <span className="font-semibold">{ids.length} selected</span>
      {["sms", "email"].map((k) => <label key={k} className="inline-flex items-center gap-1"><input type="checkbox" data-testid={`swi-ch-${k}`} checked={ch[k]} onChange={(e) => setCh({ ...ch, [k]: e.target.checked })} className="accent-indigo-500" />{k.toUpperCase()}</label>)}
      <Button data-testid="swi-send" disabled={busy} onClick={send} className="h-9 rounded-full gap-1 text-xs"><Send size={13} /> {busy ? "Sending…" : "Send training link"}</Button>
    </div>
  );
}

function Roster({ m, rows, reload }) {
  const [filter, setFilter] = useState("outstanding");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const list = useMemo(() => rows.filter((r) => (filter === "all" || (filter === "completed" ? r.status === "completed" : r.status !== "completed")) && (!q || (r.employee_name || "").toLowerCase().includes(q.toLowerCase()))), [rows, filter, q]);
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const allSel = list.length > 0 && list.every((r) => sel[r.employee_id]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        {[["outstanding", "Not completed"], ["completed", "Completed"], ["all", "All"]].map(([k, l]) => (
          <button key={k} data-testid={`swi-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 h-8 rounded-full text-xs font-semibold ${filter === k ? "bg-indigo-500 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>))}
        <Input data-testid="swi-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name" className="h-8 w-48 text-sm" />
      </div>
      <SendBar m={m} ids={ids} onSent={() => { setSel({}); reload(); }} />
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0"><tr>
              <th className="p-2 w-8"><input type="checkbox" data-testid="swi-sel-all" checked={allSel} onChange={(e) => setSel((s) => ({ ...s, ...Object.fromEntries(list.map((r) => [r.employee_id, e.target.checked])) }))} /></th>
              <th className="p-2 text-left">Worker</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">First attempt (HR only)</th><th className="p-2"></th></tr></thead>
            <tbody>{list.map((r) => (
              <tr key={r.employee_id} className="border-t border-slate-50" data-testid={`swi-row-${r.employee_id}`}>
                <td className="p-2"><input type="checkbox" checked={!!sel[r.employee_id]} onChange={(e) => setSel({ ...sel, [r.employee_id]: e.target.checked })} /></td>
                <td className="p-2"><div className="font-medium text-slate-800">{r.employee_name}</div><div className="text-[11px] text-slate-400">{r.department}{!r.has_mobile && " · no mobile"}{!r.has_email && " · no email"}</div></td>
                <td className="p-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${ST[r.status][1]}`}>{ST[r.status][0]}</span>
                  <div className="text-[11px] text-slate-400">{r.completed_at ? new Date(r.completed_at).toLocaleDateString("en-AU") : r.sent_at ? `sent ${new Date(r.sent_at).toLocaleDateString("en-AU")}` : ""}</div></td>
                <td className="p-2 text-xs">{r.total ? <span title={r.wrong.map((w) => `Q${w.q}: chose ${w.wrong.join(", ")} first`).join("\n")} className={r.first_attempt === r.total ? "text-teal-700 font-semibold" : "text-orange-700 font-semibold"} data-testid={`swi-first-${r.employee_id}`}>{r.first_attempt}/{r.total}{r.wrong.length ? ` · missed Q${r.wrong.map((w) => w.q).join(", Q")}` : ""}</span> : "—"}</td>
                <td className="p-2 text-right">{r.document_id && <button data-testid={`swi-doc-${r.employee_id}`} onClick={() => hrOpen(`/documents/${r.document_id}/file`)} className="text-indigo-600 inline-flex items-center gap-1 text-xs"><FileText size={13} /> Signed PDF</button>}</td>
              </tr>))}
              {!list.length && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No workers here</td></tr>}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function SwiAdmin() {
  const [mods, setMods] = useState([]);
  const [mid, setMid] = useState(null);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(false);
  const loadMods = (pick) => hrApi.get("/swi").then((r) => { setMods(r.data); setMid((cur) => pick || cur || r.data[0]?.id || null); });
  const loadRows = () => mid && hrApi.get(`/swi/${mid}/roster`).then((r) => setRows(r.data));
  useEffect(() => { loadMods(); }, []);
  useEffect(() => { loadRows(); }, [mid]);
  const m = mods.find((x) => x.id === mid);
  const preview = async () => {
    try { const r = await hrApi.post(`/swi/${mid}/send`, { preview: true }); window.open(r.data.url, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open preview"); }
  };
  return (
    <div className="space-y-4" data-testid="swi-admin">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading font-extrabold text-2xl text-slate-800 inline-flex items-center gap-2"><GraduationCap size={22} /> Training (SWI)</h1>
        <span className="ml-auto"><UploadSwi onDone={(id) => loadMods(id).then(() => setEdit(true))} /></span>
      </div>
      <p className="text-slate-500 text-sm -mt-2">Workers read the work instruction, answer a quiz they can't fail, and sign. The signed PDF is filed to their profile.</p>
      <div className="flex flex-wrap gap-2">{mods.map((x) => (
        <button key={x.id} data-testid={`swi-mod-${x.id}`} onClick={() => setMid(x.id)} className={`rounded-2xl border px-4 py-3 text-left ${x.id === mid ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white"}`}>
          <div className="font-semibold text-slate-800 text-sm">{x.name}</div>
          <div className="text-xs text-slate-500">{x.questions.length} questions · {x.completed}/{x.active_employees} completed</div>
        </button>))}</div>
      {m && <>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="swi-preview" variant="outline" onClick={preview} className="h-9 rounded-full gap-1 text-xs"><Eye size={13} /> Preview as worker</Button>
          <Button data-testid="swi-edit" variant="outline" onClick={() => setEdit(true)} className="h-9 rounded-full gap-1 text-xs"><Pencil size={13} /> Edit questions</Button>
        </div>
        {!m.questions.length && <div className="rounded-xl bg-orange-50 text-orange-800 text-sm p-3">Add the quiz questions before sending this training.</div>}
        <Roster m={m} rows={rows} reload={() => { loadRows(); loadMods(); }} />
        {edit && <QEditor key={m.id} m={m} onClose={() => setEdit(false)} onSaved={() => loadMods()} />}
      </>}
    </div>
  );
}
